import { useCallback, useEffect, useRef } from 'react';
import { useToolStore } from '../stores/toolStore';
import { useTokenStore } from '../stores/tokenStore';
import type { Point } from '@machina/core';

/**
 * MeasureOverlay
 *
 * Ethereal "Lasso of Light" measurement tool.
 * Renders an animated glowing light strand with flowing energy effects.
 * Uses SVG filters for glow and CSS animations for the ethereal flow.
 */

// CSS keyframes for the flowing light animation
const animationStyles = `
  @keyframes flowEnergy {
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -40; }
  }

  @keyframes pulseGlow {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  @keyframes shimmer {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.7; }
  }
`;

interface MeasureOverlayProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

export function MeasureOverlay({ containerRef }: MeasureOverlayProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  const activeTool = useToolStore((s) => s.activeTool);
  const measure = useToolStore((s) => s.measure);
  const startMeasure = useToolStore((s) => s.startMeasure);
  const updateMeasure = useToolStore((s) => s.updateMeasure);
  const addWaypoint = useToolStore((s) => s.addWaypoint);
  const endMeasure = useToolStore((s) => s.endMeasure);
  const clearMeasure = useToolStore((s) => s.clearMeasure);

  const gridSize = useTokenStore((s) => s.gridSize);

  // Convert client coords to SVG coords
  const getLocalPoint = useCallback(
    (clientX: number, clientY: number): Point => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const rect = containerRef.current.getBoundingClientRect();
      let x = clientX - rect.left;
      let y = clientY - rect.top;

      // Snap to grid center if enabled
      if (measure.snapToGrid) {
        x = Math.floor(x / gridSize) * gridSize + gridSize / 2;
        y = Math.floor(y / gridSize) * gridSize + gridSize / 2;
      }

      return { x, y };
    },
    [containerRef, gridSize, measure.snapToGrid]
  );

  // Calculate distance in feet (5ft per grid square)
  const calculateDistance = useCallback(
    (points: Point[]): number => {
      if (points.length < 2) return 0;

      let total = 0;
      for (let i = 1; i < points.length; i++) {
        const dx = points[i].x - points[i - 1].x;
        const dy = points[i].y - points[i - 1].y;
        const pixels = Math.sqrt(dx * dx + dy * dy);
        total += pixels;
      }

      // Convert pixels to feet (1 grid = 5ft)
      return Math.round((total / gridSize) * 5);
    },
    [gridSize]
  );

  // Handle mouse events
  useEffect(() => {
    if (activeTool !== 'measure' || !containerRef.current) return;

    const container = containerRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // Left click only
      const point = getLocalPoint(e.clientX, e.clientY);
      startMeasure(point);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!measure.isActive) return;
      const point = getLocalPoint(e.clientX, e.clientY);
      updateMeasure(point);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      endMeasure();
    };

    const handleClick = (e: MouseEvent) => {
      // Double-click to add waypoint
      if (e.detail === 2 && measure.isActive) {
        const point = getLocalPoint(e.clientX, e.clientY);
        addWaypoint(point);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearMeasure();
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    activeTool,
    measure.isActive,
    containerRef,
    getLocalPoint,
    startMeasure,
    updateMeasure,
    addWaypoint,
    endMeasure,
    clearMeasure,
  ]);

  // Don't render if not measuring
  if (activeTool !== 'measure') return null;
  if (!measure.startPoint || !measure.endPoint) return null;

  // Build points array for distance calculation
  const allPoints = [
    measure.startPoint,
    ...measure.waypoints,
    measure.endPoint,
  ];
  const distance = calculateDistance(allPoints);

  // Build path data
  const pathData = allPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Calculate label position (midpoint of last segment)
  const lastIndex = allPoints.length - 1;
  const labelX = (allPoints[lastIndex - 1].x + allPoints[lastIndex].x) / 2;
  const labelY = (allPoints[lastIndex - 1].y + allPoints[lastIndex].y) / 2;

  return (
    <svg
      ref={svgRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 100,
      }}
    >
      {/* Inject animation styles */}
      <style>{animationStyles}</style>

      {/* SVG Filters for ethereal glow effects */}
      <defs>
        {/* Main glow filter - soft golden aura */}
        <filter id="lassoGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur2" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur3" />
          <feMerge>
            <feMergeNode in="blur3" />
            <feMergeNode in="blur2" />
            <feMergeNode in="blur1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Point glow filter - more intense */}
        <filter id="pointGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Gradient for the light strand */}
        <linearGradient id="lassoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ffeaa7" stopOpacity="0.9" />
          <stop offset="30%" stopColor="#f8e3a1" stopOpacity="1" />
          <stop offset="50%" stopColor="#fffef0" stopOpacity="1" />
          <stop offset="70%" stopColor="#f8e3a1" stopOpacity="1" />
          <stop offset="100%" stopColor="#ffeaa7" stopOpacity="0.9" />
        </linearGradient>

        {/* Point gradient - ethereal gold */}
        <radialGradient id="pointGradient">
          <stop offset="0%" stopColor="#fffef0" />
          <stop offset="50%" stopColor="#f8e3a1" />
          <stop offset="100%" stopColor="#d4af37" />
        </radialGradient>
      </defs>

      {/* Layer 1: Outer glow (widest, most diffuse) */}
      <path
        d={pathData}
        fill="none"
        stroke="#d4af37"
        strokeWidth={16}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.15}
        filter="url(#lassoGlow)"
      />

      {/* Layer 2: Middle glow layer */}
      <path
        d={pathData}
        fill="none"
        stroke="#f8e3a1"
        strokeWidth={8}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.4}
        filter="url(#lassoGlow)"
        style={{
          animation: 'pulseGlow 2s ease-in-out infinite',
        }}
      />

      {/* Layer 3: Core bright line with flowing animation */}
      <path
        d={pathData}
        fill="none"
        stroke="url(#lassoGradient)"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="12 8"
        style={{
          animation: measure.isActive ? 'flowEnergy 0.8s linear infinite' : 'none',
          filter: 'url(#lassoGlow)',
        }}
      />

      {/* Layer 4: Inner bright core (solid) */}
      <path
        d={pathData}
        fill="none"
        stroke="#fffef0"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.9}
      />

      {/* Shimmer particles along the path (decorative) */}
      {measure.isActive && measure.waypoints.length === 0 && (
        <>
          {[0.2, 0.4, 0.6, 0.8].map((offset, i) => {
            const x = measure.startPoint!.x + (measure.endPoint!.x - measure.startPoint!.x) * offset;
            const y = measure.startPoint!.y + (measure.endPoint!.y - measure.startPoint!.y) * offset;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={2}
                fill="#fffef0"
                filter="url(#pointGlow)"
                style={{
                  animation: `shimmer ${1 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            );
          })}
        </>
      )}

      {/* Waypoints - glowing golden orbs */}
      {measure.waypoints.map((point, i) => (
        <g key={i}>
          {/* Outer glow ring */}
          <circle
            cx={point.x}
            cy={point.y}
            r={10}
            fill="none"
            stroke="#d4af37"
            strokeWidth={2}
            opacity={0.5}
            filter="url(#pointGlow)"
          />
          {/* Inner orb */}
          <circle
            cx={point.x}
            cy={point.y}
            r={5}
            fill="url(#pointGradient)"
            filter="url(#pointGlow)"
          />
        </g>
      ))}

      {/* Start point - emerald green glow */}
      <g>
        <circle
          cx={measure.startPoint.x}
          cy={measure.startPoint.y}
          r={14}
          fill="none"
          stroke="#2ecc71"
          strokeWidth={2}
          opacity={0.4}
          filter="url(#pointGlow)"
        />
        <circle
          cx={measure.startPoint.x}
          cy={measure.startPoint.y}
          r={8}
          fill="#2ecc71"
          filter="url(#pointGlow)"
        />
        <circle
          cx={measure.startPoint.x}
          cy={measure.startPoint.y}
          r={3}
          fill="#a8ffc4"
        />
      </g>

      {/* End point - ruby red glow */}
      <g>
        <circle
          cx={measure.endPoint.x}
          cy={measure.endPoint.y}
          r={14}
          fill="none"
          stroke="#e74c3c"
          strokeWidth={2}
          opacity={0.4}
          filter="url(#pointGlow)"
          style={{
            animation: measure.isActive ? 'pulseGlow 1s ease-in-out infinite' : 'none',
          }}
        />
        <circle
          cx={measure.endPoint.x}
          cy={measure.endPoint.y}
          r={8}
          fill="#e74c3c"
          filter="url(#pointGlow)"
        />
        <circle
          cx={measure.endPoint.x}
          cy={measure.endPoint.y}
          r={3}
          fill="#ffaaaa"
        />
      </g>

      {/* Distance label - ornate floating card */}
      <g transform={`translate(${labelX}, ${labelY - 20})`}>
        {/* Glow behind label */}
        <ellipse
          cx={0}
          cy={0}
          rx={40}
          ry={18}
          fill="#d4af37"
          opacity={0.2}
          filter="url(#lassoGlow)"
        />
        {/* Label background */}
        <rect
          x={-35}
          y={-14}
          width={70}
          height={28}
          rx={6}
          fill="rgba(20, 15, 10, 0.9)"
          stroke="#d4af37"
          strokeWidth={1.5}
        />
        {/* Distance text */}
        <text
          x={0}
          y={5}
          textAnchor="middle"
          fontSize={15}
          fontWeight={700}
          fontFamily="var(--font-mono)"
          fill="#f8e3a1"
          style={{ textShadow: '0 0 8px rgba(212, 175, 55, 0.8)' }}
        >
          {distance} ft
        </text>
      </g>

      {/* Instructions - subtle, ethereal */}
      {measure.isActive && (
        <text
          x={10}
          y={20}
          fontSize={11}
          fill="#a08060"
          fontFamily="var(--font-family)"
          opacity={0.7}
        >
          Click to measure • Double-click for waypoints • Esc to cancel
        </text>
      )}
    </svg>
  );
}
